"use client";


import { cn } from "../../cn";
import type { SegmentedControlProps } from "./types";
import { TamiLoadingIndicator } from "../tami-loading-indicator/tami-loading-indicator.web";

/**
 * Faixa de escolha única com rótulo — a "Visão" da folha "Filtros e visão"
 * no celular (TAM-264): Grade/Lista, Cartões/Tabela. Largura total, uma célula
 * por opção. A gêmea nativa está ao lado.
 *
 * No tom `primary` (seletor de idioma) o ativo é um destaque creme que
 * desliza até a opção escolhida, e `loadingValue` troca o ícone dela por um
 * indicador enquanto a escolha grava — sem isso o clique parecia não fazer
 * nada até a página recarregar.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onValueChange,
  label,
  className,
  tone = "muted",
  loadingValue = null,
}: SegmentedControlProps<T>) {
  const sliding = tone === "primary";
  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );
  const busy = loadingValue !== null;

  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-busy={busy || undefined}
      className={cn("relative grid h-11 gap-1 rounded-md border border-border p-1", className)}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {sliding ? (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-1 left-1 top-1 rounded-sm bg-primary transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{
            width: `calc((100% - 0.5rem - ${(options.length - 1) * 0.25}rem) / ${options.length})`,
            transform: `translateX(calc(${activeIndex} * (100% + 0.25rem)))`,
          }}
        />
      ) : null}
      {options.map((option) => {
        const active = option.value === value;
        const loading = option.value === loadingValue;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={option.testId}
            disabled={busy}
            onClick={() => onValueChange(option.value)}
            className={cn(
              "relative inline-flex items-center justify-center gap-2 rounded-sm text-sm transition-colors duration-300",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default",
              active
                ? sliding
                  ? "font-medium text-primary-foreground"
                  : "bg-muted font-medium text-foreground"
                : "text-muted-foreground enabled:hover:text-foreground",
            )}
          >
            {loading ? <TamiLoadingIndicator variant="compact" className="size-4 shrink-0" /> : option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
